/**
 * Payment-form rules, kept out of the component so they can be tested without
 * a browser.
 *
 * The server remains the authority on the balance, the invoice's status and
 * reference uniqueness. These checks only stop an obviously invalid submission
 * before it reaches the wire.
 */

/** Methods the API accepts. Order is the order the picker shows. */
export const PAYMENT_METHODS = [
  "cash",
  "card",
  "upi",
  "netbanking",
  "cheque",
  "wallet",
  "other",
] as const;

export type PaymentMethodValue = (typeof PAYMENT_METHODS)[number];

/** Display labels for the method picker and summaries. */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodValue, string> = {
  cash: "Cash",
  card: "Card",
  upi: "UPI",
  netbanking: "Net banking",
  cheque: "Cheque",
  wallet: "Wallet",
  other: "Other",
};

export interface PaymentValidationInput {
  amount: string;
  paymentDate: string;
  method: string;
  reference: string;
  outstanding: number;
  /** An explicit advance may exceed the invoice balance (FR-25.4). */
  allowOverpayment?: boolean;
}

export interface PaymentValidationErrors {
  amount?: string;
  payment_date?: string;
  method?: string;
  reference?: string;
}

export interface RefundValidationInput extends PaymentValidationInput {
  refundable: number;
}

/** FR-25.2: every method but cash carries a reference. */
export function referenceRequired(method: string): boolean {
  return method !== "cash";
}

/** Whether a `YYYY-MM-DD` value is a real calendar date. */
function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === value;
}

/**
 * Today as a calendar date in the account's zone.
 *
 * A payment date is a business date: an owner in Dubai recording money for a
 * Chennai studio should default to the studio's day, not their own. Falls back
 * to the browser's local date when the account zone is unavailable or invalid.
 */
export function todayInAccountZone(timeZone?: string): string {
  const now = new Date();
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(now);
      const part = (type: string): string | undefined =>
        parts.find((item) => item.type === type)?.value;
      const year = part("year");
      const month = part("month");
      const day = part("day");
      if (year && month && day) return `${year}-${month}-${day}`;
    } catch {
      // An unknown IANA name is not worth breaking the form over.
    }
  }
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** The balance left after a payment, or null when the amount is not usable. */
export function balanceAfter(outstanding: number, amount: string): number | null {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.max(0, outstanding - value);
}

export function validatePayment(input: PaymentValidationInput): PaymentValidationErrors {
  const errors: PaymentValidationErrors = {};
  const amount = input.amount.trim();
  const value = Number(amount);

  if (!amount) {
    errors.amount = "Enter the amount received.";
  } else if (!Number.isFinite(value) || value <= 0) {
    errors.amount = "Enter an amount greater than zero.";
  } else if (!input.allowOverpayment && input.outstanding > 0 && value > input.outstanding) {
    errors.amount = "This payment is more than the outstanding balance.";
  }

  if (!input.paymentDate) {
    errors.payment_date = "Choose the payment date.";
  } else if (!isCalendarDate(input.paymentDate)) {
    errors.payment_date = "Enter a valid payment date.";
  }

  if (!PAYMENT_METHODS.includes(input.method as PaymentMethodValue)) {
    errors.method = "Choose a payment method.";
  }

  if (referenceRequired(input.method) && !input.reference.trim()) {
    errors.reference = "Add a reference for this payment method.";
  }

  return errors;
}

export function validateRefund(input: RefundValidationInput): PaymentValidationErrors {
  const errors: PaymentValidationErrors = {};
  const amount = input.amount.trim();
  const value = Number(amount);

  if (!amount) {
    errors.amount = "Enter the amount to refund.";
  } else if (!Number.isFinite(value) || value <= 0) {
    errors.amount = "Enter an amount greater than zero.";
  } else if (input.refundable >= 0 && value > input.refundable) {
    errors.amount = "This refund is more than the amount available to refund.";
  }

  if (!input.paymentDate) {
    errors.payment_date = "Choose the refund date.";
  } else if (!isCalendarDate(input.paymentDate)) {
    errors.payment_date = "Enter a valid refund date.";
  }

  if (!PAYMENT_METHODS.includes(input.method as PaymentMethodValue)) {
    errors.method = "Choose a payment method.";
  }

  if (referenceRequired(input.method) && !input.reference.trim()) {
    errors.reference = "Add a reference for this refund method.";
  }

  return errors;
}
